import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { JobType } from '../graphql/types';
import type { Document } from '../graphql/types';
import { StatusDot } from '../components/ui/StatusDot';
import type { ChecklistItem } from './intake/intakeBAModel';
import { RailItem } from './Rail';
import { RAIL_HEADING_CLASS, RAIL_NOTE_CLASS } from './LoanRail';
import type { RailStatusDisplay } from './LoanRail';
import {
  buildDocumentRailRows,
  documentConfirmationsFromSaved,
  documentRailNote,
  formatConfirmed,
} from './railStatus';
import type { ConfirmationSummary } from './railStatus';
import { readWorkspaceParams, writeWorkspaceParams } from './urlState';

/**
 * DocumentRail (BA): the standard borrower documents for the loan, shown inside
 * the Workspace Rail. The heading carries a count note ("Documents · 1 missing").
 * Each row shows the document, a status dot and one line: "n pages · extracted",
 * a conflict note ("1 page · name conflict"), or "Missing" for a standard
 * document that has not been received (`buildDocumentRailRows`). When known,
 * "n of m confirmed" follows.
 *
 * Confirmations come from the `confirmations` prop, or, when `jobId` and
 * `loanId` are given, from the confirmations saved for the loan.
 *
 * Selecting a received document writes `doc` and the document's first field to
 * the URL and clears `exception`; `mode`, `loan` and unrelated params are kept
 * (Requirement 3.4). Missing rows are not selectable.
 *
 * Requirement: 3.1.
 */
export interface DocumentRailProps {
  documents: ReadonlyArray<Document>;
  /** Field confirmations per document id. Overrides the saved confirmations. */
  confirmations?: Readonly<Record<string, ConfirmationSummary | undefined>>;
  /** With `loanId`, reads "n of m confirmed" from the saved confirmations. */
  jobId?: string;
  loanId?: string;
  /** Standard document list; defaults to the borrower checklist. */
  checklist?: ReadonlyArray<ChecklistItem>;
  /** Conflict notes by document id, overriding those found in the fields. */
  conflicts?: Readonly<Record<string, string>>;
  /** Overrides the `doc` URL param as the selected document. */
  selectedDocumentId?: string | null;
  /** Called after a document is chosen, in addition to the URL update. */
  onSelect?: (documentId: string) => void;
}

export function getDocumentStatusDisplay(
  doc: Pick<Document, 'errorMessage' | 'requiresHumanReview'>
): RailStatusDisplay {
  if (doc.errorMessage) return { label: 'Failed', tone: 'danger', dot: 'danger' };
  if (doc.requiresHumanReview) return { label: 'Needs review', tone: 'warning', dot: 'warning' };
  return { label: 'Extracted', tone: 'success', dot: 'success' };
}

export function getDocumentTitle(doc: Pick<Document, 'documentType' | 'sourceFilename'>): string {
  return doc.documentType?.trim() || doc.sourceFilename?.trim() || 'Document';
}

const ROW_CLASS = 'block w-full border-l-[3px] border-transparent px-4 py-3 text-left text-sm';

export const DocumentRail: React.FC<DocumentRailProps> = ({
  documents,
  confirmations,
  jobId,
  loanId,
  checklist,
  conflicts,
  selectedDocumentId,
  onSelect,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected =
    selectedDocumentId !== undefined
      ? selectedDocumentId
      : readWorkspaceParams(searchParams, JobType.BA).doc;

  const rows = React.useMemo(
    () => buildDocumentRailRows(documents, { checklist, conflicts }),
    [documents, checklist, conflicts]
  );
  const note = documentRailNote(rows);

  // Saved confirmations are read on every render so a confirm made in Extract shows at once.
  const summaries =
    confirmations ??
    (jobId && loanId ? documentConfirmationsFromSaved(jobId, loanId, documents) : undefined);

  const choose = (documentId: string, firstFieldId: string | null) => {
    if (documentId !== selected || !searchParams.get('field')) {
      setSearchParams(
        writeWorkspaceParams(searchParams, {
          doc: documentId,
          field: firstFieldId,
          exception: null,
        })
      );
    }
    onSelect?.(documentId);
  };

  return (
    <div data-testid="document-rail">
      <p className={RAIL_HEADING_CLASS}>
        <span>Documents</span>
        {note && (
          <span className={RAIL_NOTE_CLASS} data-testid="rail-note">
            {note}
          </span>
        )}
      </p>
      {rows.map((row) => {
        const body = (
          <span className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-bold text-gray-900">{row.title}</span>
              <span
                className={`mt-0.5 block text-xs ${row.state === 'missing' ? 'font-bold text-[#991b1b]' : 'text-gray-500'}`}
                data-testid="document-meta"
              >
                {row.meta}
                {row.documentId && summaries?.[row.documentId]?.total ? (
                  <>
                    {' · '}
                    <span data-testid="document-confirmed">
                      {formatConfirmed(summaries[row.documentId]!)}
                    </span>
                  </>
                ) : null}
              </span>
            </span>
            <StatusDot tone={row.dot} size={10} label={row.meta} />
          </span>
        );
        if (row.documentId === null) {
          return (
            <div key={row.key} data-testid="document-missing" className={ROW_CLASS}>
              {body}
            </div>
          );
        }
        const documentId = row.documentId;
        return (
          <RailItem
            key={row.key}
            selected={documentId === selected}
            onClick={() => choose(documentId, row.firstFieldId)}
          >
            {body}
          </RailItem>
        );
      })}
    </div>
  );
};

export default DocumentRail;
